import { Box, Button, DialogProps } from "@mui/material";
import { DynamicField, Issue } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { PropsWithChildren, useCallback, useState } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { useDeepCompareMemoize } from "use-deep-compare-effect";

import { ConfirmDialog } from "./ConfirmDialog";
import { Dialog } from "./Dialog";
import { IssueListButton } from "./Forms/IssueListButton";
import { StateFieldsForm } from "./Forms/StateFieldsForm";

export const DynamicFormDialog = (props: DynamicFormDialogContentProps) => {
  // avoid form being initialized too early if dialog is not open yet
  if (props.open) {
    return <DynamicFormDialogContent {...props} />;
  }
};

type DynamicFormDialogContentProps = PropsWithChildren<
  {
    title: string;
    displayOnly?: boolean;
    persistedIssues: Issue[];
    fields: DynamicField[];
    onSubmit: SubmitHandler<DynamicField[]>;
    onClose: () => void;
    validator: (input: DynamicField[]) => Promise<Issue[]>;
  } & Omit<DialogProps, "onSubmit" | "onClose">
>;

type DynamicFormDialogContentInput = {
  fields: DynamicField[];
};

const DynamicFormDialogContent = ({
  title,
  persistedIssues,
  fields,
  onSubmit,
  onClose,
  validator,
  children,
  displayOnly,
  ...dialogProps
}: DynamicFormDialogContentProps) => {
  const fieldNamePrefix: keyof DynamicFormDialogContentInput = "fields";
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const form = useValidatingForm<DynamicFormDialogContentInput>(
    useDeepCompareMemoize({ fields }),
    persistedIssues,
    ({ fields }) => validator(fields),
    [fieldNamePrefix],
    { isDisplayOnly: displayOnly }
  );
  const {
    formState: { isSubmitting, isDirty, isSubmitSuccessful },
    handleSubmit,
    issues,
  } = form;
  const hasPendingChanges = isDirty && !isSubmitSuccessful;

  const handleFormSubmit = useCallback(
    () => handleSubmit(({ fields }) => onSubmit(fields))(),
    [handleSubmit, onSubmit]
  );

  const handleDialogClose = useCallback(() => {
    if (hasPendingChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasPendingChanges, onClose]);

  return (
    <>
      <Dialog
        {...dialogProps}
        open
        onClose={handleDialogClose}
        maxWidth="md"
        title={title}
        noPrimaryAction={displayOnly ?? false}
        additionalPrimaryActionElements={
          !displayOnly
            ? [<IssueListButton key="issue-list-button" issues={issues} />]
            : [
                <Button key="done-button" onClick={handleDialogClose}>
                  Done
                </Button>,
              ]
        }
        primaryActionButtonProps={{
          onClick: handleFormSubmit,
          loading: isSubmitting,
        }}
      >
        <Box>
          {children && <Box sx={{ mb: 3 }}>{children}</Box>}
          <FormProvider {...form}>
            <StateFieldsForm
              fields={fields}
              namePrefix={fieldNamePrefix}
              applyDynamicLayout={true}
            />
          </FormProvider>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={showConfirmClose}
        onConfirm={onClose}
        onClose={() => setShowConfirmClose(false)}
        header="Are you sure you want to leave? You have unsaved changes."
        confirmText="Yes, I'm Sure"
      />
    </>
  );
};
